package se.mittpass.shared.api

import kotlinx.serialization.Serializable

@Serializable
data class WorkplaceDto(
    val id: Long,
    val name: String,
    val dayStartHour: Int = 0,
    val dayEndHour: Int = 24
)

@Serializable
data class UpdateWorkplaceRequest(
    val dayStartHour: Int? = null,
    val dayEndHour: Int? = null
)

@Serializable
data class InterestedUserDto(val id: Long, val email: String)

// ── Break Duration Helper ──
fun calculateDefaultBreakMinutes(durationMinutes: Int): Int = when {
    durationMinutes <= 180 -> 0
    durationMinutes <= 420 -> 30
    else -> 60
}

@Serializable
data class SlotDto(
    val id: Long,
    val workplaceId: Long,
    val day: String,
    val startTime: String,
    val endTime: String,
    val needCoverage: Boolean,
    val notifyStaff: Boolean,
    val assignedToUserId: Long? = null,
    val assignedToEmail: String? = null,
    val interestedUsers: List<InterestedUserDto> = emptyList(),
    val breakMinutes: Int = 0
)

@Serializable
data class CreateSlotRequest(
    val day: String,
    val startTime: String,
    val endTime: String,
    val needCoverage: Boolean = true,
    val notifyStaff: Boolean = false,
    val assignedToUserId: Long? = null,
    val breakMinutes: Int = 0
)

@Serializable
data class AssignSlotRequest(
    val userId: Long?
)

@Serializable
data class DayPatternItem(
    val dayOfWeek: Int,
    val startTime: String,
    val endTime: String,
    val assignedToUserId: Long? = null,
    val role: String? = null,
    val breakMinutes: Int = 0
)

@Serializable
data class GenerateBulkScheduleRequest(
    val startDate: String,
    val numberOfWeeks: Int,
    val patterns: List<DayPatternItem>,
    val overwriteExisting: Boolean = false
)

@Serializable
data class UpdateSlotRequest(
    val day: String? = null,
    val startTime: String? = null,
    val endTime: String? = null,
    val needCoverage: Boolean? = null,
    val notifyStaff: Boolean? = null,
    val assignedToUserId: Long? = -9999L,
    val breakMinutes: Int? = null
)

@Serializable
data class MyClaimDto(
    val id: Long,
    val slotId: Long,
    val workplaceId: Long,
    val workplaceName: String,
    val day: String,
    val startTime: String,
    val endTime: String
)

@Serializable
data class RegisterRequest(
    val organisationName: String,
    val workplaceName: String,
    val email: String,
    val password: String,
    val orgNumber: String? = null,
    val firstName: String? = null,
    val lastName: String? = null
)

@Serializable
data class LoginRequest(val email: String, val password: String)

@Serializable
data class AuthResponse(
    val token: String = "",
    val refreshToken: String? = null,
    val message: String? = null
)

@Serializable
data class RefreshTokenRequest(val refreshToken: String)

// ── Staff management ──

@Serializable
data class StaffMemberDto(
    val userId: Long,
    val email: String,
    val role: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val personalNumber: String? = null,
    val phone: String? = null,
    val active: Boolean = true,
    val emailVerified: Boolean = false,
    /** Hourly rate in öre (1 SEK = 100 öre). Null = not set. */
    val hourlyRateOre: Int? = null,
    /** Monthly salary in öre. Null = not set. */
    val monthlySalaryOre: Int? = null,
    val taxTable: String? = null,
    val taxRatePct: Int? = null,
    val obType: String = "default",
    val obEveningValue: Int? = null,
    val obNightValue: Int? = null,
    val obWeekendValue: Int? = null,
    val annualVacationDays: Int = 25,
    val customRoles: List<CustomRoleDto> = emptyList(),
    val color: String? = null
)

@Serializable
data class SetActiveRequest(val active: Boolean)

@Serializable
data class InviteStaffRequest(val email: String)

@Serializable
data class AddStaffRequest(
    val firstName: String,
    val lastName: String,
    val personalNumber: String,
    val phone: String,
    val email: String,
    val password: String? = null,
    val color: String? = null
)

@Serializable
data class UpdateStaffRequest(
    val firstName: String? = null,
    val lastName: String? = null,
    val personalNumber: String? = null,
    val phone: String? = null,
    val email: String? = null,
    val color: String? = null
)

// ── Attendance / Personalliggare ──

@Serializable
data class AttendanceDto(
    val id: Long,
    val userId: Long,
    val userEmail: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val personalNumber: String? = null,
    val workplaceId: Long,
    val slotId: Long? = null,
    val checkInTime: String = "",
    val checkOutTime: String? = null,
    val createdAt: Long,
    val updatedAt: Long,
    val modifiedByUserId: Long? = null
)

@Serializable
data class CheckInRequest(
    val workplaceId: Long,
    val slotId: Long? = null,
    val latitude: Double? = null,
    val longitude: Double? = null
)

@Serializable
data class CheckOutRequest(val attendanceId: Long)

@Serializable
data class UpdateAttendanceRequest(
    val checkInTime: String? = null,
    val checkOutTime: String? = null
)

@Serializable
data class AuditLogEntryDto(
    val id: Long,
    val attendanceId: Long,
    val fieldName: String,
    val oldValue: String? = null,
    val newValue: String? = null,
    val changedByUserId: Long,
    val changedByEmail: String? = null,
    val changedAt: Long
)

@Serializable
data class SnapshotEntryDto(
    val userId: Long,
    val email: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val personalNumber: String? = null,
    val checkInTime: String = "",
    val slotId: Long? = null
)

// ── Payroll ──

@Serializable
data class PayrollRecordDto(
    val id: Long,
    val attendanceId: Long? = null,
    val userId: Long,
    val userEmail: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val workplaceId: Long,
    val day: String,
    val startTime: String,
    val endTime: String,
    val breakMinutes: Int = 0,
    val netMinutes: Int = 0,
    val notes: String? = null,
    val approved: Boolean = false,
    val slotStartTime: String? = null,
    val slotEndTime: String? = null,
    val slotBreakMinutes: Int? = null,
    val checkInTime: String? = null,
    val checkOutTime: String? = null
)

@Serializable
data class CreatePayrollRecordRequest(
    val attendanceId: Long? = null,
    val userId: Long,
    val day: String,
    val startTime: String,
    val endTime: String,
    val breakMinutes: Int = 0,
    val notes: String? = null
)

@Serializable
data class UpdatePayrollRecordRequest(
    val startTime: String? = null,
    val endTime: String? = null,
    val breakMinutes: Int? = null,
    val notes: String? = null,
    val approved: Boolean? = null
)

@Serializable
data class BillingStatusResponse(
    val planStatus: String,
    val trialEndsAt: Long? = null,
    val daysLeftInTrial: Long? = null,
    val activeUsersThisMonth: Int = 0,
    val estimatedInvoiceSek: Double = 0.0,
    val hasSubscription: Boolean = false,
    val stripeEnabled: Boolean = false
)

@Serializable
data class UpdateStaffSalaryRequest(
    val hourlyRateOre: Int? = null,
    val monthlySalaryOre: Int? = null,
    val taxTable: String? = null,
    val taxRatePct: Int? = null,
    val obType: String = "default",
    val obEveningValue: Int? = null,
    val obNightValue: Int? = null,
    val obWeekendValue: Int? = null,
    val annualVacationDays: Int? = null
)

@Serializable
data class PayrollSummaryDto(
    val userId: Long,
    val firstName: String? = null,
    val lastName: String? = null,
    val userEmail: String,
    val hourlyRateOre: Int? = null,
    val monthlySalaryOre: Int? = null,
    val taxTable: String? = null,
    val taxRatePct: Int? = null,
    val obType: String = "default",
    val normalMinutes: Int = 0,
    val obEveningMinutes: Int = 0,
    val obNightMinutes: Int = 0,
    val obWeekendMinutes: Int = 0,
    val overtimeMinutes: Int = 0,
    val vacationMinutes: Int = 0,
    val sickMinutes: Int = 0,
    val vabMinutes: Int = 0,
    val leaveMinutes: Int = 0,
    val vacationDaysTotal: Int = 25,
    val vacationDaysUsed: Double = 0.0,
    val vacationDaysRemaining: Double = 25.0,
    val basePaySek: Double = 0.0,
    val obSurchargeSek: Double = 0.0,
    val vacationPaySek: Double = 0.0,
    val sickPaySek: Double = 0.0,
    val grossPaySek: Double = 0.0,
    val taxDeductionSek: Double = 0.0,
    val netPaySek: Double = 0.0,
    val employerContributionSek: Double = 0.0,
    val totalCostSek: Double = 0.0,
    val totalSek: Double = 0.0 // alias for totalCostSek for backward compatibility
)

@Serializable
data class PayslipDto(
    val id: Long,
    val workplaceId: Long,
    val workplaceName: String? = null,
    val userId: Long,
    val userName: String? = null,
    val userEmail: String? = null,
    val personalNumber: String? = null,
    val periodStart: String,
    val periodEnd: String,
    val paymentDate: String,
    val grossPaySek: Double,
    val basePaySek: Double,
    val obPaySek: Double,
    val vacationPaySek: Double,
    val sickPaySek: Double = 0.0,
    val taxDeductionSek: Double,
    val netPaySek: Double,
    val employerContributionSek: Double,
    val totalCostSek: Double,
    val workedMinutes: Int,
    val obEveningMinutes: Int = 0,
    val obNightMinutes: Int = 0,
    val obWeekendMinutes: Int = 0,
    val overtimeMinutes: Int = 0,
    val sickMinutes: Int = 0,
    val vabMinutes: Int = 0,
    val leaveMinutes: Int = 0,
    val vacationDaysTotal: Int = 25,
    val vacationDaysUsed: Double = 0.0,
    val vacationDaysRemaining: Double = 25.0,
    val status: String = "draft",
    val notes: String? = null,
    val createdAt: Long,
    val publishedAt: Long? = null
)

@Serializable
data class GeneratePayslipsRequest(
    val periodStart: String,
    val periodEnd: String,
    val paymentDate: String,
    val autoPublish: Boolean = false,
    val userId: Long? = null
)

// ── Staff stats ──

@Serializable
data class StaffStatsDto(
    val totalWorkedMinutes: Long = 0,
    val totalPaidMinutes: Long = 0,
    val preliminaryMinutes: Long = 0,
    val upcomingSlots: Int = 0,
    // Current payroll period & earnings for staff
    val periodStart: String = "",
    val periodEnd: String = "",
    val nextPayday: String = "",
    val hourlyRateOre: Int? = null,
    val monthlySalaryOre: Int? = null,
    val approvedMinutes: Int = 0,
    val approvedSickMinutes: Int = 0,
    val approvedVacationDaysUsed: Double = 0.0,
    val vacationDaysTotal: Int = 25,
    val vacationDaysRemaining: Double = 25.0,
    val approvedBasePaySek: Double = 0.0,
    val approvedObPaySek: Double = 0.0,
    val approvedVacationPaySek: Double = 0.0,
    val approvedSickPaySek: Double = 0.0,
    val approvedGrossPaySek: Double = 0.0,
    val approvedTaxDeductionSek: Double = 0.0,
    val approvedNetPaySek: Double = 0.0,
    val approvedEmployerContributionSek: Double = 0.0,
    val approvedTotalCostSek: Double = 0.0,
    val taxTable: String? = null,
    val taxRatePct: Int? = null,
    val pendingGrossPaySek: Double = 0.0
)

// ── Absence management (Frånvaro) ──

@Serializable
data class AbsenceRequestDto(
    val id: Long,
    val userId: Long,
    val userEmail: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val workplaceId: Long,
    val type: String,
    val startDate: String,
    val endDate: String,
    val note: String? = null,
    val status: String,
    val reviewedByUserId: Long? = null,
    val reviewedByEmail: String? = null,
    val reviewedAt: Long? = null,
    val createdAt: Long
)

@Serializable
data class CreateAbsenceRequest(
    val type: String,
    val startDate: String,
    val endDate: String,
    val note: String? = null,
    val userId: Long? = null,
    val status: String? = null
)

@Serializable
data class ReviewAbsenceRequest(
    val status: String
)

@Serializable
data class UpdateAbsenceRequest(
    val type: String,
    val startDate: String,
    val endDate: String,
    val note: String? = null,
    val status: String
)


// ── Shift swap (Vaktbyte) ──

@Serializable
data class ShiftSwapDto(
    val id: Long,
    val slotId: Long,
    val slotDay: String,
    val slotStartTime: String,
    val slotEndTime: String,
    val fromUserId: Long,
    val fromUserEmail: String,
    val fromFirstName: String? = null,
    val fromLastName: String? = null,
    val toUserId: Long? = null,
    val toUserEmail: String? = null,
    val status: String,
    val message: String? = null,
    val createdAt: Long
)

@Serializable
data class CreateSwapRequest(
    val slotId: Long,
    val message: String? = null
)

@Serializable
data class RespondSwapRequest(
    val action: String
)

// ── Analytics ──

@Serializable
data class AnalyticsDto(
    val totalStaff: Int = 0,
    val totalWorkedHours: Double = 0.0,
    val totalApprovedPayrollHours: Double = 0.0,
    val pendingAbsenceRequests: Int = 0,
    val activeAbsences: Int = 0,
    val currentlyCheckedIn: Int = 0,
    val slotsThisWeek: Int = 0,
    val unclaimedSlotsThisWeek: Int = 0,
    val dailyHours: List<DailyHoursDto> = emptyList(),
    val staffDetails: List<StaffDetailDto> = emptyList(),
    val checkedInDetails: List<CheckedInDetailDto> = emptyList(),
    val workedHoursPerPerson: List<PersonHoursDto> = emptyList(),
    val payrollHoursPerPerson: List<PersonHoursDto> = emptyList(),
    val slotsThisWeekDetails: List<SlotDetailDto> = emptyList(),
    val unclaimedSlotDetails: List<SlotDetailDto> = emptyList(),
    val pendingAbsenceDetails: List<AbsenceDetailDto> = emptyList(),
    val activeAbsenceDetails: List<AbsenceDetailDto> = emptyList()
)

@Serializable
data class DailyHoursDto(
    val date: String,
    val hours: Double,
    val persons: List<PersonDayDto> = emptyList()
)

@Serializable
data class PersonDayDto(val name: String, val hours: Double)

@Serializable
data class StaffDetailDto(val name: String, val email: String, val role: String)

@Serializable
data class CheckedInDetailDto(val name: String, val email: String, val since: String)

@Serializable
data class PersonHoursDto(val name: String, val email: String, val hours: Double)

@Serializable
data class SlotDetailDto(val day: String, val startTime: String, val endTime: String, val assignedTo: String? = null)

@Serializable
data class AbsenceDetailDto(val name: String, val type: String, val startDate: String, val endDate: String, val note: String? = null)

// ── Fortnox / Multi-system payroll export ──

@Serializable
data class FortnoxExportRequest(
    val from: String,
    val to: String
)

@Serializable
data class PayrollExportSettingsDto(
    val workplaceId: Long = 0,
    val targetSystem: String = "fortnox",
    val fileFormat: String = "paxml",
    val employeeIdMode: String = "personal_number",
    val codeMode: String = "tidkod",
    val codeNormal: String = "TID",
    val codeOb1: String = "OB1",
    val codeOb2: String = "OB2",
    val codeOb3: String = "OB3",
    val onlyApproved: Boolean = false
)


// ── Phase 2: Team communication ──

@Serializable
data class MessageDto(
    val id: Long,
    val workplaceId: Long,
    val senderUserId: Long,
    val senderName: String,
    val senderEmail: String,
    val content: String,
    val pinned: Boolean = false,
    val createdAt: Long
)

@Serializable
data class CreateMessageRequest(val content: String)

// ── Phase 2: Collective agreement (Kollektivavtal) ──

@Serializable
data class CollectiveAgreementDto(
    val id: Long = 0,
    val workplaceId: Long = 0,
    val obEveningPct: Int = 20,
    val obNightPct: Int = 40,
    val obWeekendPct: Int = 50,
    val obHolidayPct: Int = 100,
    val overtimeThresholdDailyMinutes: Int = 480,
    val overtimeRatePct: Int = 50,
    val eveningStart: String = "18:00",
    val eveningEnd: String = "22:00",
    val nightStart: String = "22:00",
    val nightEnd: String = "06:00",
    val paydayDayOfMonth: Int = 25,
    val cutoffDayOfMonth: Int = 15
)

@Serializable
data class SaveCollectiveAgreementRequest(
    val obEveningPct: Int = 20,
    val obNightPct: Int = 40,
    val obWeekendPct: Int = 50,
    val obHolidayPct: Int = 100,
    val overtimeThresholdDailyMinutes: Int = 480,
    val overtimeRatePct: Int = 50,
    val eveningStart: String = "18:00",
    val eveningEnd: String = "22:00",
    val nightStart: String = "22:00",
    val nightEnd: String = "06:00",
    val paydayDayOfMonth: Int = 25,
    val cutoffDayOfMonth: Int = 15
)

@Serializable
data class PayrollOBBreakdownDto(
    val payrollRecordId: Long,
    val userName: String,
    val day: String,
    val normalMinutes: Int = 0,
    val eveningMinutes: Int = 0,
    val nightMinutes: Int = 0,
    val weekendMinutes: Int = 0,
    val overtimeMinutes: Int = 0,
    val obSurchargePercent: Double = 0.0
)

// ── Phase 2: Compliance warnings ──

@Serializable
data class ComplianceWarningDto(
    val id: Long,
    val workplaceId: Long,
    val userId: Long,
    val userName: String,
    val ruleCode: String,
    val ruleLabel: String,
    val severity: String,
    val message: String,
    val referenceDate: String,
    val acknowledged: Boolean = false,
    val createdAt: Long
)

// ── Phase 2: Time bank (Tidbank) ──

@Serializable
data class TimeBankEntryDto(
    val id: Long,
    val userId: Long,
    val userName: String,
    val workplaceId: Long,
    val type: String,
    val minutes: Int,
    val description: String? = null,
    val referenceDate: String,
    val createdAt: Long
)

@Serializable
data class CreateTimeBankEntryRequest(
    val userId: Long,
    val type: String,
    val minutes: Int,
    val description: String? = null,
    val referenceDate: String
)

@Serializable
data class TimeBankBalanceDto(
    val userId: Long,
    val userName: String,
    val earnedMinutes: Int = 0,
    val usedMinutes: Int = 0,
    val balanceMinutes: Int = 0
)

// ── Phase 2: GPS check-in ──

@Serializable
data class GeoCheckInRequest(
    val workplaceId: Long,
    val slotId: Long? = null,
    val latitude: Double,
    val longitude: Double
)

@Serializable
data class WorkplaceGeoDto(
    val workplaceId: Long,
    val name: String,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val geofenceRadiusMeters: Int = 200
)

@Serializable
data class UpdateWorkplaceGeoRequest(
    val latitude: Double?,
    val longitude: Double?,
    val geofenceRadiusMeters: Int = 200
)

// ── Phase 2: Contractors (Underentreprenörer) ──

@Serializable
data class StaffMemberFullDto(
    val userId: Long,
    val email: String,
    val role: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val personalNumber: String? = null,
    val phone: String? = null,
    val isContractor: Boolean = false,
    val companyName: String? = null,
    val contractorOrgNumber: String? = null
)

@Serializable
data class AddContractorRequest(
    val firstName: String,
    val lastName: String,
    val personalNumber: String,
    val phone: String,
    val email: String,
    val password: String,
    val companyName: String,
    val contractorOrgNumber: String? = null
)

// ── Phase 2: Multi-workplace dashboard ──

@Serializable
data class MultiWorkplaceSummaryDto(
    val workplaceId: Long,
    val workplaceName: String,
    val totalStaff: Int = 0,
    val currentlyCheckedIn: Int = 0,
    val pendingAbsences: Int = 0,
    val activeWarnings: Int = 0,
    val slotsThisWeek: Int = 0
)

// ── Phase 3: Tips (Drickshantering) ──

@Serializable
data class TipDto(
    val id: Long,
    val workplaceId: Long,
    val day: String,
    val totalAmount: Int,
    val currency: String = "SEK",
    val distributionMethod: String = "equal",
    val createdByName: String,
    val shares: List<TipShareDto> = emptyList(),
    val createdAt: Long
)

@Serializable
data class TipShareDto(
    val userId: Long,
    val userName: String,
    val amount: Int,
    val workedMinutes: Int = 0
)

@Serializable
data class CreateTipRequest(
    val day: String,
    val totalAmount: Int,
    val distributionMethod: String = "equal"
)

// ── Phase 3: Projects (Projektbaserad tidrapportering) ──

@Serializable
data class ProjectDto(
    val id: Long,
    val workplaceId: Long,
    val name: String,
    val code: String? = null,
    val clientName: String? = null,
    val hourlyRate: Int? = null,
    val active: Boolean = true,
    val totalMinutes: Int = 0,
    val createdAt: Long
)

@Serializable
data class CreateProjectRequest(
    val name: String,
    val code: String? = null,
    val clientName: String? = null,
    val hourlyRate: Int? = null
)

@Serializable
data class ProjectTimeEntryDto(
    val id: Long,
    val projectId: Long,
    val projectName: String,
    val userId: Long,
    val userName: String,
    val day: String,
    val minutes: Int,
    val description: String? = null,
    val createdAt: Long
)

@Serializable
data class CreateTimeEntryRequest(
    val projectId: Long,
    val day: String,
    val minutes: Int,
    val description: String? = null
)

// ── Phase 3: Expenses (Utläggshantering) ──

@Serializable
data class ExpenseDto(
    val id: Long,
    val userId: Long,
    val userName: String,
    val workplaceId: Long,
    val amount: Int,
    val currency: String = "SEK",
    val category: String,
    val description: String,
    val receiptUrl: String? = null,
    val day: String,
    val status: String = "pending",
    val reviewedByName: String? = null,
    val createdAt: Long
)

@Serializable
data class CreateExpenseRequest(
    val amount: Int,
    val category: String,
    val description: String,
    val receiptUrl: String? = null,
    val day: String
)

@Serializable
data class ReviewExpenseRequest(val status: String)

// ── Phase 3: API keys & Webhooks ──

@Serializable
data class ApiKeyDto(
    val id: Long,
    val workplaceId: Long,
    val name: String,
    val keyPrefix: String,
    val scopes: String,
    val active: Boolean = true,
    val lastUsedAt: Long? = null,
    val createdAt: Long
)

@Serializable
data class CreateApiKeyRequest(
    val name: String,
    val scopes: String = "read"
)

@Serializable
data class ApiKeyCreatedDto(
    val id: Long,
    val key: String,
    val name: String
)

@Serializable
data class WebhookDto(
    val id: Long,
    val workplaceId: Long,
    val url: String,
    val events: String,
    val active: Boolean = true,
    val lastTriggeredAt: Long? = null,
    val createdAt: Long
)

@Serializable
data class CreateWebhookRequest(
    val url: String,
    val events: String,
    val secret: String? = null
)

// ── Phase 3: HR module ──

@Serializable
data class HrDocumentDto(
    val id: Long,
    val userId: Long,
    val userName: String,
    val workplaceId: Long,
    val name: String,
    val category: String,
    val fileUrl: String? = null,
    val uploadedAt: Long
)

@Serializable
data class CreateHrDocumentRequest(
    val userId: Long,
    val name: String,
    val category: String,
    val fileUrl: String? = null
)

@Serializable
data class CertificationDto(
    val id: Long,
    val userId: Long,
    val userName: String,
    val workplaceId: Long,
    val name: String,
    val issuer: String? = null,
    val issuedDate: String? = null,
    val expiryDate: String? = null,
    val status: String = "valid",
    val createdAt: Long
)

@Serializable
data class CreateCertificationRequest(
    val userId: Long,
    val name: String,
    val issuer: String? = null,
    val issuedDate: String? = null,
    val expiryDate: String? = null
)

@Serializable
data class OnboardingChecklistDto(
    val id: Long,
    val workplaceId: Long,
    val title: String,
    val items: List<String> = emptyList(),
    val createdAt: Long
)

@Serializable
data class CreateChecklistRequest(
    val title: String,
    val items: List<String>
)

@Serializable
data class OnboardingProgressDto(
    val checklistId: Long,
    val checklistTitle: String,
    val userId: Long,
    val userName: String,
    val totalItems: Int,
    val completedCount: Int,
    val completed: Boolean = false
)

// ── Phase 3: Invoices (Faktureringsunderlag) ──

@Serializable
data class InvoiceDto(
    val id: Long,
    val workplaceId: Long,
    val projectId: Long? = null,
    val projectName: String? = null,
    val clientName: String,
    val invoiceNumber: String? = null,
    val fromDate: String,
    val toDate: String,
    val totalHours: Double = 0.0,
    val hourlyRate: Int = 0,
    val totalAmount: Int = 0,
    val currency: String = "SEK",
    val status: String = "draft",
    val notes: String? = null,
    val createdAt: Long
)

@Serializable
data class CreateInvoiceRequest(
    val projectId: Long? = null,
    val clientName: String,
    val invoiceNumber: String? = null,
    val fromDate: String,
    val toDate: String,
    val hourlyRate: Int = 0,
    val notes: String? = null
)

@Serializable
data class UpdateInvoiceStatusRequest(val status: String)

// ── Phase 3: Schedule optimization ──

@Serializable
data class ScheduleSuggestionDto(
    val slotId: Long,
    val day: String,
    val startTime: String,
    val endTime: String,
    val suggestedUserId: Long,
    val suggestedUserName: String,
    val reason: String,
    val score: Double = 0.0
)

@Serializable
data class ApplySuggestionRequest(
    val slotId: Long,
    val userId: Long
)

// ── Food Safety (Egenkontroll / HACCP) ──

@Serializable
data class FoodSafetyTemplateDto(
    val id: Long,
    val workplaceId: Long,
    val name: String,
    val category: String,
    val unit: String = "C",
    val minValue: Double? = null,
    val maxValue: Double? = null,
    val frequency: String = "daily",
    val active: Boolean = true,
    val sortOrder: Int = 0,
    val createdAt: Long,
    val stepResolution: Double = 0.1,
    val quickPresets: String? = null
)

@Serializable
data class CreateFoodSafetyTemplateRequest(
    val name: String,
    val category: String = "temperature",
    val unit: String = "C",
    val minValue: Double? = null,
    val maxValue: Double? = null,
    val frequency: String = "daily",
    val sortOrder: Int = 0,
    val stepResolution: Double = 0.1,
    val quickPresets: String? = null
)

@Serializable
data class UpdateFoodSafetyTemplateRequest(
    val name: String? = null,
    val category: String? = null,
    val unit: String? = null,
    val minValue: Double? = null,
    val maxValue: Double? = null,
    val frequency: String? = null,
    val active: Boolean? = null,
    val sortOrder: Int? = null,
    val stepResolution: Double? = null,
    val quickPresets: String? = null
)

@Serializable
data class FoodSafetyLogDto(
    val id: Long,
    val templateId: Long,
    val templateName: String,
    val category: String,
    val unit: String,
    val workplaceId: Long,
    val loggedByUserId: Long,
    val loggedByName: String,
    val valueNumeric: Double? = null,
    val valueText: String? = null,
    val valueOk: Boolean? = null,
    val isDeviation: Boolean = false,
    val deviationAction: String? = null,
    val loggedAt: Long
)

@Serializable
data class CreateFoodSafetyLogRequest(
    val templateId: Long,
    val valueNumeric: Double? = null,
    val valueText: String? = null,
    val valueOk: Boolean? = null,
    val deviationAction: String? = null
)

@Serializable
data class FoodSafetyTodayItemDto(
    val template: FoodSafetyTemplateDto,
    val logs: List<FoodSafetyLogDto> = emptyList(),
    val completedToday: Boolean = false
)

@Serializable
data class FoodSafetyReportDto(
    val totalExpected: Int = 0,
    val totalCompleted: Int = 0,
    val compliancePercent: Double = 0.0,
    val deviationCount: Int = 0,
    val deviationsWithAction: Int = 0,
    val templates: List<FoodSafetyTemplateDto> = emptyList(),
    val deviations: List<FoodSafetyLogDto> = emptyList(),
    val recentLogs: List<FoodSafetyLogDto> = emptyList(),
    val temperatureTrends: List<TemperatureTrendDto> = emptyList()
)

@Serializable
data class TemperatureTrendDto(
    val templateId: Long,
    val templateName: String,
    val minValue: Double? = null,
    val maxValue: Double? = null,
    val dailyPoints: List<TrendPointDto> = emptyList()
)

@Serializable
data class TrendPointDto(
    val day: String,
    val avgValue: Double,
    val minValue: Double,
    val maxValue: Double,
    val count: Int
)

@Serializable
data class AdminOverviewDto(
    val totalOrgs: Int,
    val totalUsers: Int,
    val trialOrgs: Int,
    val activeOrgs: Int,
    val pastDueOrgs: Int,
    val canceledOrgs: Int
)

@Serializable
data class AdminOrgDto(
    val id: Long,
    val name: String,
    val orgNumber: String?,
    val planStatus: String,
    val trialEndsAt: Long?,
    val workplaceCount: Int,
    val userCount: Int,
    val createdAt: Long
)

@Serializable
data class AdminUserDto(
    val userId: Long,
    val email: String,
    val role: String,
    val firstName: String?,
    val lastName: String?,
    val active: Boolean,
    val orgName: String?,
    val createdAt: Long
)

@Serializable
data class SetPlanStatusRequest(val planStatus: String)

@Serializable
data class AdminUpdateOrgRequest(
    val name: String,
    val orgNumber: String?
)

@Serializable
data class AdminUpdateUserRequest(
    val firstName: String?,
    val lastName: String?,
    val email: String,
    val role: String
)

@Serializable
data class FeedbackRequest(
    val name: String,
    val email: String,
    val message: String,
    val workplaceId: Long? = null,
    val context: String? = null
)

@Serializable
data class CustomRoleDto(
    val id: Long,
    val workplaceId: Long,
    val name: String
)

@Serializable
data class CreateCustomRoleRequest(
    val name: String
)

@Serializable
data class AssignRolesRequest(
    val roleIds: List<Long>
)

@Serializable
data class RequirementDayCountDto(
    val dayOfWeek: Int,
    val roleId: Long,
    val count: Int
)

@Serializable
data class WorkplaceRequirementDto(
    val id: Long,
    val workplaceId: Long,
    val name: String,
    val startDate: String, // YYYY-MM-DD
    val endDate: String,   // YYYY-MM-DD
    val days: List<RequirementDayCountDto>
)

@Serializable
data class CreateWorkplaceRequirementRequest(
    val name: String,
    val startDate: String,
    val endDate: String,
    val days: List<RequirementDayCountDto>
)

